export type TranscriptSegment = {
  id: string;
  start_seconds: number;
  end_seconds: number;
  source_text: string;
  zh_text: string;
  turns?: VerbatimTranscriptTurn[];
};

export type VerbatimTranscriptTurn = {
  id: string;
  speaker_id: string;
  source_speaker: string;
  speaker: string;
  start_seconds: number | null;
  chapter_id: string | null;
  source_text: string;
  zh_text: string;
};

export type TranscriptChapter = {
  id: string;
  source_title: string;
  title: string;
  start_seconds: number;
  end_seconds: number;
};

export type RenderedTranscriptTurn = {
  speaker: string;
  sourceSpeaker: string;
  text: string;
  sourceText: string;
};

export type TranscriptDocument = {
  version: number;
  sourceLanguage: string;
  language: string;
  model: string;
  timing?: "estimated";
  chapters?: TranscriptChapter[];
  segments: TranscriptSegment[];
};

export type DialogueTurn = {
  speaker: "host" | "analyst";
  text: string;
  sourceSegments: string[];
};

export type DialogueDocument = {
  version: number;
  title: string;
  summary: string;
  tags: string[];
  turns: DialogueTurn[];
};

export type ArticleSection = {
  title: string;
  paragraphs: string[];
  sourceSegments: string[];
  sourceStartSeconds: number;
};

export type ArticleDocument = {
  version: number;
  promptVersion: string;
  model: string;
  title: string;
  dek: string;
  introduction: string;
  sections: ArticleSection[];
  conclusion: string;
  takeaways: string[];
  sourceUrl: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isTranscriptSegment(value: unknown): value is TranscriptSegment {
  return isRecord(value)
    && typeof value.id === "string"
    && /^S\d{4}$/u.test(value.id)
    && typeof value.start_seconds === "number"
    && value.start_seconds >= 0
    && typeof value.end_seconds === "number"
    && value.end_seconds > value.start_seconds
    && typeof value.source_text === "string"
    && value.source_text.length > 0
    && typeof value.zh_text === "string"
    && value.zh_text.length > 0
    && (value.turns === undefined
      || (Array.isArray(value.turns) && value.turns.every(isVerbatimTranscriptTurn)));
}

function isVerbatimTranscriptTurn(value: unknown): value is VerbatimTranscriptTurn {
  return isRecord(value)
    && typeof value.id === "string"
    && typeof value.speaker_id === "string"
    && value.speaker_id.length > 0
    && typeof value.source_speaker === "string"
    && value.source_speaker.length > 0
    && typeof value.speaker === "string"
    && value.speaker.length > 0
    && (value.start_seconds === null
      || (typeof value.start_seconds === "number" && value.start_seconds >= 0))
    && (value.chapter_id === null || typeof value.chapter_id === "string")
    && typeof value.source_text === "string"
    && value.source_text.length > 0
    && typeof value.zh_text === "string"
    && value.zh_text.length > 0;
}

function isTranscriptChapter(value: unknown): value is TranscriptChapter {
  return isRecord(value)
    && typeof value.id === "string"
    && /^C\d{4}$/u.test(value.id)
    && typeof value.source_title === "string"
    && value.source_title.length > 0
    && typeof value.title === "string"
    && value.title.length > 0
    && typeof value.start_seconds === "number"
    && value.start_seconds >= 0
    && typeof value.end_seconds === "number"
    && value.end_seconds > value.start_seconds;
}

export function isTranscriptDocument(
  value: unknown,
): value is TranscriptDocument {
  return isRecord(value)
    && typeof value.version === "number"
    && typeof value.sourceLanguage === "string"
    && typeof value.language === "string"
    && typeof value.model === "string"
    && (value.timing === undefined || value.timing === "estimated")
    && (value.chapters === undefined
      || (Array.isArray(value.chapters)
        && value.chapters.length > 0
        && value.chapters.every(isTranscriptChapter)))
    && Array.isArray(value.segments)
    && value.segments.length > 0
    && value.segments.every(isTranscriptSegment);
}

const speakerNames: Record<string, string> = {
  "Lex Fridman": "莱克斯·弗里德曼",
  "Jensen Huang": "黄仁勋",
  "Dwarkesh Patel": "德瓦克什·帕特尔",
  "Dario Amodei": "达里奥·阿莫代",
};

function labeledBlocks(value: string): Array<{ speaker: string; text: string }> {
  const blocks: Array<{ speaker: string; text: string }> = [];
  for (const rawBlock of value.split(/\n\s*\n/u)) {
    const block = rawBlock.trim();
    const match = block.match(/^([^:\n：]{1,80})[:：]\s*([\s\S]+)$/u);
    if (!match) return [];
    blocks.push({ speaker: match[1].trim(), text: match[2].trim() });
  }
  return blocks;
}

export function transcriptTurns(segment: TranscriptSegment): RenderedTranscriptTurn[] {
  if (segment.turns?.length) {
    return segment.turns.map((turn) => ({
      speaker: turn.speaker,
      sourceSpeaker: turn.source_speaker,
      text: turn.zh_text,
      sourceText: turn.source_text,
    }));
  }
  const sourceBlocks = labeledBlocks(segment.source_text);
  const translatedBlocks = labeledBlocks(segment.zh_text);
  if (sourceBlocks.length > 0 && sourceBlocks.length === translatedBlocks.length) {
    return sourceBlocks.map((source, index) => ({
      speaker: speakerNames[source.speaker] ?? translatedBlocks[index].speaker,
      sourceSpeaker: source.speaker,
      text: translatedBlocks[index].text,
      sourceText: source.text,
    }));
  }
  return [{
    speaker: "原节目",
    sourceSpeaker: "Original program",
    text: segment.zh_text,
    sourceText: segment.source_text,
  }];
}

function isDialogueTurn(value: unknown): value is DialogueTurn {
  return isRecord(value)
    && (value.speaker === "host" || value.speaker === "analyst")
    && typeof value.text === "string"
    && value.text.length > 0
    && Array.isArray(value.sourceSegments)
    && value.sourceSegments.every(
      (segment) => typeof segment === "string" && /^S\d{4}$/u.test(segment),
    );
}

export function isDialogueDocument(
  value: unknown,
): value is DialogueDocument {
  if (
    !isRecord(value)
    || typeof value.version !== "number"
    || typeof value.title !== "string"
    || typeof value.summary !== "string"
    || !Array.isArray(value.tags)
    || !value.tags.every((tag) => typeof tag === "string")
    || !Array.isArray(value.turns)
    || value.turns.length < 2
    || !value.turns.every(isDialogueTurn)
  ) {
    return false;
  }
  const turns = value.turns as DialogueTurn[];
  return turns.every(
    (turn, index) => index === 0 || turn.speaker !== turns[index - 1].speaker,
  );
}

function isArticleSection(value: unknown): value is ArticleSection {
  return isRecord(value)
    && typeof value.title === "string"
    && value.title.length > 0
    && Array.isArray(value.paragraphs)
    && value.paragraphs.length > 0
    && value.paragraphs.every(
      (paragraph) => typeof paragraph === "string" && paragraph.length > 0,
    )
    && Array.isArray(value.sourceSegments)
    && value.sourceSegments.length > 0
    && value.sourceSegments.every(
      (segment) => typeof segment === "string" && /^S\d{4}$/u.test(segment),
    )
    && typeof value.sourceStartSeconds === "number"
    && value.sourceStartSeconds >= 0;
}

export function isArticleDocument(value: unknown): value is ArticleDocument {
  return isRecord(value)
    && typeof value.version === "number"
    && typeof value.promptVersion === "string"
    && typeof value.model === "string"
    && typeof value.title === "string"
    && value.title.length > 0
    && typeof value.dek === "string"
    && value.dek.length > 0
    && typeof value.introduction === "string"
    && value.introduction.length > 0
    && Array.isArray(value.sections)
    && value.sections.length > 0
    && value.sections.every(isArticleSection)
    && typeof value.conclusion === "string"
    && value.conclusion.length > 0
    && Array.isArray(value.takeaways)
    && value.takeaways.length > 0
    && value.takeaways.every(
      (takeaway) => typeof takeaway === "string" && takeaway.length > 0,
    )
    && typeof value.sourceUrl === "string"
    && /^https?:\/\//u.test(value.sourceUrl);
}

export function podcastSectionTitle(title: string, sectionNumber: number): string {
  const trimmed = title.trim();
  const numbered = trimmed.match(
    /^0*(\d{1,3})\s*(?:[.．、:：|｜)）]|[-—–])\s*(\S[\s\S]*)$/u,
  );
  if (numbered && Number(numbered[1]) === sectionNumber) {
    return numbered[2].trim();
  }
  const chapter = trimmed.match(
    /^第\s*0*(\d{1,3})\s*(?:章|节|部分)\s*(?:[.．、:：|｜]|[-—–])?\s*(\S[\s\S]*)$/u,
  );
  if (chapter && Number(chapter[1]) === sectionNumber) {
    return chapter[2].trim();
  }
  return trimmed;
}

export function formatPodcastTime(totalSeconds: number): string {
  const bounded = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(bounded / 3600);
  const minutes = Math.floor((bounded % 3600) / 60);
  const seconds = bounded % 60;
  return hours > 0
    ? `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}
